import { FC, ReactNode } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { Box, Button, Typography } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import { useDateFormat } from "@/shared/hooks";
import { ContentAccessErrorTypeType } from "@/types/graphql/site.generated";

interface AccessErrorScreenProps {
  error: ContentAccessErrorTypeType;
  accessStartAt?: string | null;
  poster?: string | null;
  /** Кнопка «Разобраться с помощником» (Task 15): экран сам не знает про
   * features/assistant (боковой FSD-слой), содержимое готовит вызывающая
   * сторона (ContentPlayer.renderAssistantAction) и передаёт сюда слотом. */
  assistantAction?: ReactNode;
}

export const AccessErrorScreen: FC<AccessErrorScreenProps> = ({
  error,
  accessStartAt,
  poster,
  assistantAction,
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { formatDateTime } = useDateFormat();

  const isGuests =
    error === ContentAccessErrorTypeType.ContentIsNotAvailableForGuestsType;
  const isCountry =
    error === ContentAccessErrorTypeType.ContentIsNotAvailableInCountryType;

  // Иконка и заголовок экрана зависят от типа ошибки доступа; вынесены из
  // JSX, чтобы не городить вложенные тернарники (запрещены eslint-правилом).
  let icon = <AccessTimeFilledIcon sx={{ fontSize: 48 }} />;
  let title = t("player.accessError.notAvailableYetTitle");
  if (isCountry) {
    icon = <PublicOffIcon sx={{ fontSize: 48 }} />;
    title = t("player.accessError.countryTitle");
  } else if (isGuests) {
    icon = <LockIcon sx={{ fontSize: 48 }} />;
    title = t("player.accessError.guestsTitle");
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#000",
        backgroundImage: poster ? `url(${poster})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          backgroundColor: "rgba(0,0,0,0.65)",
          color: "#fff",
          textAlign: "center",
          px: 2,
        }}
      >
        {icon}
        <Typography variant="h6">{title}</Typography>
        {!isGuests && !isCountry && accessStartAt && (
          <Typography variant="body2">
            {t("player.accessError.availableAt", {
              date: formatDateTime(accessStartAt),
            })}
          </Typography>
        )}
        {isGuests && (
          <Button
            variant="contained"
            onClick={() =>
              router.push({
                pathname: router.pathname,
                query: { ...router.query, signin: "true" },
              })
            }
          >
            {t("player.accessError.signIn")}
          </Button>
        )}
        {assistantAction}
      </Box>
    </Box>
  );
};
